import {utils} from "xlsx-js-style";
import {
    copiaProfunda,
    fixDecimals,
    getColNameByNumber,
    getCurrentMonth,
    getNumberMonth,
    separateNumberByComma,
    sortAlphaNumerically
} from "@/common/constants/functions";

/**
 * Calcula el valor de T dado un valor de EA
 * @param {number} ea - Valor de EA
 * @param {{listIngresos: number[], listEgresos: number[], interesesRealesPagados: number[]}} params - Parámetros adicionales
 * @returns {number} Valor de T
 */
const calculateT = (ea, {listIngresos, listEgresos, interesesRealesPagados}) => {
    const ed = (1 + ea) ** (1 / 365) - 1;
    const listSaldoExtracto = [];
    const listSaldoRendimiento = [];
    const listIntereses = [];
    for (let index = 0; index < listIngresos.length; index++) {
        const ingreso = listIngresos[index];
        const egreso = listEgresos[index];
        const saldoExtracto = ingreso - egreso + (listSaldoExtracto.length ? listSaldoExtracto[listSaldoExtracto.length - 1] : 0);
        listSaldoExtracto.push(saldoExtracto);
        const saldoRendimiento = (listSaldoRendimiento.length ? listSaldoRendimiento[listSaldoRendimiento.length - 1] : 0) + ingreso - egreso + (listIntereses.length ? listIntereses[listIntereses.length - 1] : 0);
        listSaldoRendimiento.push(saldoRendimiento);
        if (listIntereses.length) {
            const i = ed * listSaldoRendimiento[listSaldoRendimiento.length - 1]
            listIntereses.push(i)
        } else listIntereses.push(0);
    }
    return listIntereses.reduce((res, value) => res + value, 0) - interesesRealesPagados;
}

/**
 * Encuentra el valor de EA
 * @param {{listIngresos: number[], listEgresos: number[], interesesRealesPagados: number[]}} params - Parámetros de entrada
 * @returns {number} Valor de EA
 */
export const findEA = ({listIngresos, listEgresos, interesesRealesPagados}) => {
    const params = {listIngresos, listEgresos, interesesRealesPagados};
    // Valor inicial de EA
    let x0 = 0.01;
    // Tolerancia para considerar que se ha encontrado la raíz
    const epsilon = 0.001;
    // Número máximo de iteraciones permitidas
    let max_iterations = 100;
    let index = 0;
    while (index < max_iterations) {
        const T = calculateT(x0, params);
        if (Math.abs(T) < epsilon) {
            return x0;
        } else {
            const derivative = (calculateT(x0 + 0.0001, params) - T) / 0.0001
            // Aproximación de la derivada
            x0 = x0 - T / derivative
            index += 1
        }
    }
    return 0;
}

/**
 Calcula el extracto bancario mensual a partir de los datos recibidos
 @param {Object} informacionCalculoMovimientos - La información de los movimientos a utilizar para calcular el extracto bancario mensual.
 @param {string} informacionCalculoMovimientos.nombre_banco - El nombre del banco de la cuenta bancaria.
 @param {string} informacionCalculoMovimientos.cuenta_bancaria - El número de la cuenta bancaria.
 @param {string} informacionCalculoMovimientos.tipo_cuenta - El tipo de la cuenta bancaria.
 @param {number} informacionCalculoMovimientos.año.mes.tasa_e_a_entidad - La tasa efectiva anual pagada a la entidad financiera.
 @param {number} informacionCalculoMovimientos.año.mes.tasa_e_a_banco - La tasa efectiva anual pagada por el banco.
 @param {number} informacionCalculoMovimientos.año.mes.rendimiento_periodo - El rendimiento del periodo.
 @param {number} informacionCalculoMovimientos.año.mes.ganancia_interes_adicional - La ganancia de intereses adicionales.
 @param {number} informacionCalculoMovimientos.año.mes.rendimiento_periodo_pagado_mes_siguiente - El rendimiento del periodo pagado el mes siguiente.
 @returns {Object.<string, Object.<string, Object>>} - Un objeto que representa el extracto bancario mensual. Contiene la información de los saldos y los intereses de los movimientos bancarios organizados por año y mes.
 */
export const calculateExtract = (informacionCalculoMovimientos) => {
    const filesInfo = [];
    const data = {};
    const newDataExtracto = {};
    const {nombre_banco, cuenta_bancaria, nombre_cuenta,  tipo_cuenta, abonosMesesAnterioresRecibidos} =
        informacionCalculoMovimientos;
    delete informacionCalculoMovimientos.nombre_banco;
    delete informacionCalculoMovimientos.manejo_cuenta
    delete informacionCalculoMovimientos.cuenta_bancaria;
    delete informacionCalculoMovimientos.nombre_cuenta;
    delete informacionCalculoMovimientos.extracto_id;
    delete informacionCalculoMovimientos.tipo_cuenta;
    delete informacionCalculoMovimientos.abonosMesesAnterioresDados;
    delete informacionCalculoMovimientos.abonosMesesAnterioresRecibidos;
    delete informacionCalculoMovimientos.statusCode;
    for (const year in informacionCalculoMovimientos) {
        const workbook = utils.book_new();
        let fileName = '';
        const informacionCalculoMovimientosYear =
            informacionCalculoMovimientos[year];
        newDataExtracto[year] = {};
        for (const mes in informacionCalculoMovimientosYear) {
            fileName = `${cuenta_bancaria}_${nombre_banco}_${year}_${mes}_resultados_extractos.xlsx`;
            const contentExcelIzq = [];
            const contentExcelDer = [];
            const tasaEAEntidad =
                informacionCalculoMovimientos[year][mes]['tasa_e_a_entidad'];
            const tasaED = Math.pow(1 + tasaEAEntidad / 100, 1 / 365) - 1;
            const tasaEDPercentage = (tasaED * 100).toFixed(5);
            const rendimientosDelPeriodo = Number(
                informacionCalculoMovimientos[year][mes]['rendimiento_periodo'],
            );
            const gananciaInteresesAdicionales = Number(
                informacionCalculoMovimientos[year][mes][
                    'ganancia_interes_adicional'
                    ],
            );
            const rendimientosPeriodoPagadoMesSiguiente = Number(
                informacionCalculoMovimientos[year][mes][
                    'rendimiento_periodo_pagado_mes_siguiente'
                    ],
            );
            const interesesRealesPagados =
                rendimientosDelPeriodo +
                gananciaInteresesAdicionales +
                rendimientosPeriodoPagadoMesSiguiente;
            const tasaEABanco =
                informacionCalculoMovimientos[year][mes]['tasa_e_a_banco'];
            const tasaEDBanco =
                Math.pow(1 + tasaEABanco / 100, 1 / 365) - 1;
            const tasaEDBancoPercentage = (tasaEDBanco * 100).toFixed(5);
            const listSaldoExtracto = [];
            const listSaldoRend = [];
            const listSaldoBanco = [];
            const listIntereses = [];
            const listInteresesBanco = [];
            for (
                let i = 0;
                i <
                Object.keys(informacionCalculoMovimientos[year][mes]['movimientos'])
                    .length;
                i++
            ) {
                const dia = Object.keys(
                    informacionCalculoMovimientos[year][mes]['movimientos'],
                )[i];
                // TODO CALCULAR
                const informacionDia =
                    informacionCalculoMovimientos[year][mes]['movimientos'][dia];
                const ingreso = parseFloat(informacionDia['ingreso']
                    ? informacionDia['ingreso']
                    : '0');
                const egreso = parseFloat(informacionDia['egreso']
                    ? informacionDia['egreso']
                    : '0');
                const rendimiento = ingreso - egreso;
                const saldoExtracto =
                    rendimiento +
                    (listSaldoExtracto[listSaldoExtracto.length - 1] || 0);
                const saldoRend =
                    (listSaldoRend[listSaldoRend.length - 1] || 0) +
                    rendimiento +
                    (listIntereses[listIntereses.length - 1] || 0);
                const saldoBanco =
                    (listSaldoBanco[listSaldoBanco.length - 1] || 0) +
                    rendimiento +
                    (listInteresesBanco[listInteresesBanco.length - 1] || 0);
                const interes = i === 0 ? 0 : tasaED * saldoRend;
                const interesBanco = i === 0 ? 0 : tasaEDBanco * saldoBanco;
                listSaldoExtracto.push(saldoExtracto);
                listSaldoRend.push(saldoRend);
                listSaldoBanco.push(saldoBanco);
                listIntereses.push(interes);
                listInteresesBanco.push(interesBanco);
                contentExcelIzq.push({
                    DIA: dia === '0' ? '' : dia,
                    INGRESOS: separateNumberByComma(ingreso.toFixed(2)),
                    PAGOS: separateNumberByComma(egreso.toFixed(2)),
                    'SALDO EXTRACT': separateNumberByComma(saldoExtracto.toFixed(2)),
                    'SALDO + REND/DIA': separateNumberByComma(saldoRend.toFixed(2)),
                    INTERESES: separateNumberByComma(interes.toFixed(2)),
                });
                contentExcelDer.push({
                    DIA: dia === '0' ? 'Saldo ant.' : dia,
                    INGRESOS: separateNumberByComma(ingreso.toFixed(2)),
                    PAGOS: separateNumberByComma(egreso.toFixed(2)),
                    SALDO: separateNumberByComma(saldoBanco.toFixed(2)),
                    INTERESES: separateNumberByComma(interesBanco.toFixed(2)),
                });
            }
            const sumInteresesBanco = listInteresesBanco.reduce(
                (partialSum, a) => partialSum + a,
                0,
            );
            newDataExtracto[year][mes] = {
                rendimiento_periodo: rendimientosDelPeriodo.toFixed(2),
                ganancia_interes_adicional: gananciaInteresesAdicionales.toFixed(2),
                rendimiento_periodo_pagado_mes_siguiente: rendimientosPeriodoPagadoMesSiguiente.toFixed(2),
                interes_tasa_pactada: sumInteresesBanco.toFixed(2),
                tasa_efectiva_anual_real_pagada: tasaEAEntidad,
            };
            const mesName = getCurrentMonth(Number(mes)).toUpperCase();
            const worksheet = buildExcel({
                listIntereses,
                mes,
                year,
                rendimientosDelPeriodo,
                gananciaInteresesAdicionales,
                rendimientosPeriodoPagadoMesSiguiente,
                interesesRealesPagados,
                tasaEABanco,
                sumInteresesBanco,
                tasaEDBancoPercentage,
                cuenta_bancaria,
                nombre_cuenta,
                tipo_cuenta,
                abonosMesesAnterioresRecibidos,
                nombre_banco,
                tasaEAEntidad,
                tasaEDPercentage,
                contentExcelIzq,
                contentExcelDer,
                informacionCalculoMovimientos,
                mesName
            });
            utils.book_append_sheet(workbook, worksheet, mesName);
        }
        filesInfo.push([workbook, fileName, {
            type: 'binary',
            compression: true,
            bookType: 'xlsx',
        }, year, Object.keys(informacionCalculoMovimientosYear)]);
        data[year] = {};
        for (const mes in workbook['Sheets']) {
            const infoMes = copiaProfunda(workbook['Sheets'][mes]);
            delete infoMes['!ref'];
            delete infoMes['!cols'];
            delete infoMes['!rows'];
            const allData = sortAlphaNumerically(infoMes);
            let currentColumn = '';
            const cols = [];
            for (const data of allData) {
                const value = Object.values(data)[0]['v'];
                const classContainer = Object.values(data)[0]['classContainer'] ? Object.values(data)[0]['classContainer'] : false;
                const classText = Object.values(data)[0]['classText'] ? Object.values(data)[0]['classText'] : false;
                const isInput = Object.values(data)[0]['isInput'] ? Object.values(data)[0]['isInput'] : false;
                const idInput = Object.values(data)[0]['isInput'] ? Object.values(data)[0]['idInput'] : false;
                const column = Object.keys(data)[0].match(/^[A-z]+/)[0];
                if (!currentColumn || column !== currentColumn) {
                    currentColumn = column;
                    cols.push([]);
                }
                cols[cols.length - 1].push({isInput, idInput, classContainer, classText, value});
            }
            const rows = [];
            for (const i in cols) {
                for (const j in cols[i]) {
                    if (!rows[j]) rows[j] = [];
                    rows[j][i] = cols[i][j];
                }
            }
            data[year][getNumberMonth(mes)] = rows;
        }
    }
    return {filesInfo, data, newDataExtracto};
}

/**
 Genera un archivo de Excel con los datos de una cuenta bancaria y sus movimientos.
 @param {Object} allData Objeto con los datos de la cuenta bancaria y sus movimientos.
 @returns {Promise} Retorna una promesa con un objeto de tipo Blob que representa el archivo de Excel generado.
 @example
 const allData = {...}; // Objeto con los datos de la cuenta bancaria y sus movimientos.
 */
function buildExcel(allData) {
    const {
        listIntereses,
        mes,
        year,
        rendimientosDelPeriodo,
        gananciaInteresesAdicionales,
        rendimientosPeriodoPagadoMesSiguiente,
        interesesRealesPagados,
        tasaEABanco,
        sumInteresesBanco,
        tasaEDBancoPercentage,
        cuenta_bancaria,
        nombre_cuenta,
        tipo_cuenta,
        abonosMesesAnterioresRecibidos,
        nombre_banco,
        tasaEAEntidad,
        tasaEDPercentage,
        contentExcelIzq,
        contentExcelDer,
        informacionCalculoMovimientos,
        mesName
    } = allData;
    // TODO DATOS
    const sumIntereses = listIntereses.reduce(
        (partialSum, a) => partialSum + a,
        0,
    );
    const cuentaBancaria = cuenta_bancaria;
    const tipoCuenta =
        tipo_cuenta === 'ahorro'
            ? 'CUENTA DE AHORROS'
            : tipo_cuenta === 'corriente'
                ? 'CUENTA CORRIENTE'
                : 'CUENTA DE NÓMINA';
    // TODO ENCABEZADOS LADO IZQ
    const worksheet = utils.aoa_to_sheet([
        // A1
        [`BANCO ${nombre_banco.toString().toUpperCase()}`, `MES: ${mesName}`, '', '', '', ''],
        // A2
        [`CUENTA BANCARIA`, nombre_cuenta, cuentaBancaria, `${mesName}-${year}`, tipoCuenta, ''],
        // A3
        ['T.I.R', `${fixDecimals(tasaEAEntidad)}%`, '', '', '', ''],
        // A4
        ['TASA E.D.', `${fixDecimals(tasaEDPercentage)}%`, '', '', '', ''],
    ]);
    if (!contentExcelIzq.length) contentExcelIzq.push({
        DIA: '0',
        INGRESOS: '0',
        PAGOS: '0',
        'SALDO EXTRACT': '0',
        'SALDO + REND/DIA': '0',
        INTERESES: '0',
    });
    if (!contentExcelDer.length) contentExcelDer.push({
        DIA: '0',
        INGRESOS: '0',
        PAGOS: '0',
        SALDO: '0',
        INTERESES: '0',
    });
    // TODO DATOS LADO IZQ
    utils.sheet_add_json(worksheet, contentExcelIzq, {origin: 'A5'});
    // TODO FOOTER LADO IZQ

    const contentFooterIzq = [
        //A38
        ['', '', '', '', 'TOTAL INTERESES CALCULADOS', separateNumberByComma(sumIntereses.toFixed(2))],
        [
            //A39
            'INTERESES REALES PAGADOS',
            //B39
            'RENDIMIENTOS PAGADOS',
            //C39
            'GANANCIA INTERESES ADICIONALES',
            //D39
            '',
            //E39
            'RENDIMIENTOS DEL PERIODO PAGADOS EN EL MES SIGUIENTE',
            //F39
            ''
        ],
        //A40
        [
            '',
            separateNumberByComma(rendimientosDelPeriodo.toFixed(2)),
            separateNumberByComma(gananciaInteresesAdicionales.toFixed(2)),
            '',
            separateNumberByComma(rendimientosPeriodoPagadoMesSiguiente.toFixed(2)),
            separateNumberByComma(interesesRealesPagados.toFixed(2)),
        ],
        //A41
        [
            'DIFERENCIA POR COBRAR',
            '',
            '',
            '',
            '',
            separateNumberByComma((sumIntereses - interesesRealesPagados).toFixed(2)),
        ],
        //A42
        [
            'TASA REAL PAGADA',
            `${fixDecimals(tasaEAEntidad)}%`,
            '',
            '',
            'TASA PACTADA',
            `${fixDecimals(tasaEABanco)}%`,
        ],
        //A43
        [
            '',
            '',
            '',
            '',
            'TASA NO REALIZADA',
            `${fixDecimals((tasaEABanco - tasaEAEntidad))}%`,
        ],
        ['', '', '', '', 'DIFERENCIA DE INTERESES CALCULADOS', separateNumberByComma((sumInteresesBanco - sumIntereses).toFixed(2))],
    ];
    utils.sheet_add_aoa(worksheet, contentFooterIzq, {
        origin: `A${contentExcelIzq.length + 6}`,
    });
    // TODO ENCABEZADOS LADO DER
    const columnStart = getColNameByNumber(Object.keys(contentExcelIzq[0]).length);
    utils.sheet_add_aoa(
        worksheet,
        [
            // G1
            [
                `BANCO ${nombre_banco.toString().toUpperCase()}`,
                `MES: ${mesName}`,
                '', '', ''
            ],
            // G2
            [
                `CUENTA BANCARIA`,
                nombre_cuenta,
                cuentaBancaria,
                `${mesName}-${year}`,
                tipoCuenta,
            ],
            // G3
            ['TASA E.A.', `${fixDecimals(tasaEABanco)}%`, '', '', ''],
            // G4
            ['TASA E.D.', `${fixDecimals(tasaEDBancoPercentage)}%`, '', '', ''],
        ],
        {origin: `${columnStart}1`},
    );
    // TODO DATOS LADO DER
    utils.sheet_add_json(worksheet, contentExcelDer, {
        origin: `${columnStart}5`,
    });
    // TODO FOOTER LADO DER
    const contentFooterDer = [
        //G38
        [
            '',
            '',
            '',
            'TOTAL INTERESES CALCULADOS',
            separateNumberByComma(sumInteresesBanco.toFixed(2)),
        ],
        //G39
        [
            '',
            'RENDIMIENTOS PAGADOS',
            separateNumberByComma(rendimientosDelPeriodo.toFixed(2)),
            '$ NO PAGADA',
            separateNumberByComma((sumInteresesBanco - interesesRealesPagados - abonosMesesAnterioresRecibidos.reduce((accumulator, currentValue) => accumulator + +currentValue.value, 0)).toFixed(2)),
        ],
        ['', '', '', '', ''],
        ['', '', '', '', ''],
        ['', '', '', '', ''],
        ['', '', '', '', ''],
        ['', '', '', '', ''],
    ];
    utils.sheet_add_aoa(worksheet, contentFooterDer, {
        origin: `${columnStart}${contentExcelDer.length + 6}`,
    });
    // TODO ANCHAR HORIZONTALMENTE COLUMNAS
    worksheet['!cols'] = new Array(
        Object.keys(contentExcelDer[0]).length +
        Object.keys(contentExcelIzq[0]).length,
    ).fill({wch: 20});
    return applyStyle({informacionCalculoMovimientos, year, mes, abonosMesesAnterioresRecibidos, worksheet});
}

/**
 Aplica estilos a la hoja de cálculo de acuerdo a los datos proporcionados.
 @param {object} allData - Objeto que contiene los datos necesarios para aplicar los estilos a la hoja de cálculo.
 @returns {worksheet} - Hoja de cálculo con los estilos aplicados.
 */
function applyStyle(allData) {
    const {informacionCalculoMovimientos, year, mes} = allData;
    let worksheet = allData['worksheet'];
    // FILAS
    const rowCabezeraMovimientos = 5;
    const cantMovimientos = Object.keys(informacionCalculoMovimientos[year][mes]['movimientos']).length;
    const rowTotalIntereses = rowCabezeraMovimientos + (cantMovimientos ? cantMovimientos : 1) + 1;
    const rowValInteresesRealesPagadosDer = rowTotalIntereses + 1;
    const rowValInteresesRealesPagadosIzq = rowValInteresesRealesPagadosDer + 1;
    const rowValDiferenciaCobrar = rowValInteresesRealesPagadosIzq + 1
    const rowValTasaRealPagada = rowValDiferenciaCobrar + 1;
    const rowValTasaNoRealizada = rowValTasaRealPagada + 1;
    const rowValDiferenciaIntereses = rowValTasaNoRealizada + 1;
    // TODO CELDAS ESTILOS
    const dataStyle = {
        worksheet,
        rowTotalIntereses,
        rowValInteresesRealesPagadosIzq,
        rowValDiferenciaCobrar,
        rowValTasaRealPagada,
        rowValTasaNoRealizada,
        rowValDiferenciaIntereses,
        rowValInteresesRealesPagadosDer,
        rowCabezeraMovimientos
    };
    worksheet = applyStyleHTML(dataStyle);
    dataStyle['worksheet'] = worksheet;
    worksheet = applyStyleExcel(dataStyle);
    return worksheet;
}

/**
 Aplica estilos en formato HTML a un objeto de datos.
 @param {Object} allData - Objeto de datos que contiene los datos de la hoja de cálculo y la información de estilo.
 @param {Object} allData.worksheet - Objeto que contiene los datos de la hoja de cálculo.
 @param {number} allData.rowTotalIntereses - Número de fila del total de intereses.
 @param {number} allData.rowValInteresesRealesPagadosIzq - Número de fila del valor de intereses reales pagados a la izquierda.
 @param {number} allData.rowValDiferenciaCobrar - Número de fila del valor de la diferencia a cobrar.
 @param {number} allData.rowValTasaRealPagada - Número de fila del valor de la tasa real pagada.
 @param {number} allData.rowValTasaNoRealizada - Número de fila del valor de la tasa no realizada.
 @param {number} allData.rowValDiferenciaIntereses - Número de fila del valor de la diferencia de intereses.
 @param {number} allData.rowValInteresesRealesPagadosDer - Número de fila del valor de intereses reales pagados a la derecha.
 @param {number} allData.rowCabezeraMovimientos - Número de fila de la cabecera de movimientos.
 @returns {worksheet} - Hoja de cálculo con los estilos aplicados.
 @example
 applyStyleHTML(allData);
 */
function applyStyleHTML(allData) {
    const {
        worksheet,
        rowTotalIntereses,
        rowValInteresesRealesPagadosIzq,
        rowValDiferenciaCobrar,
        rowValTasaRealPagada,
        rowValTasaNoRealizada,
        rowValDiferenciaIntereses,
        rowValInteresesRealesPagadosDer,
        rowCabezeraMovimientos
    } = allData;

    // TODO CABECERA IZQ
    worksheet['A1']['classText'] = 'font-bold';
    worksheet['A2']['classText'] = 'font-bold';
    worksheet['A3']['classText'] = 'font-bold';
    worksheet['A4']['classText'] = 'font-bold';
    worksheet['B1']['classText'] = 'font-bold';
    worksheet['B2']['classText'] = 'font-bold';
    worksheet['C2']['classText'] = 'font-bold text-black';
    worksheet['D2']['classText'] = 'font-bold text-black';
    // TODO CABECERA DER
    worksheet['G1']['classText'] = 'font-bold';
    worksheet['G2']['classText'] = 'font-bold';
    worksheet['G3']['classText'] = 'font-bold';
    worksheet['G4']['classText'] = 'font-bold';
    worksheet['H1']['classText'] = 'font-bold';
    worksheet['H2']['classText'] = 'font-bold';
    worksheet['I2']['classText'] = 'font-bold text-black';
    worksheet['J2']['classText'] = 'font-bold';
    // TODO CABECERA MOVIMIENTOS IZQ
    for (const column of ['A', 'B', 'C', 'D', 'E', 'F']) {
        worksheet[`${column}${rowCabezeraMovimientos}`]['classContainer'] = 'bg-black';
        worksheet[`${column}${rowCabezeraMovimientos}`]['classText'] = 'text-white';
    }
    // TODO MOVIMIENTOS IZQ
    for (let i = (rowCabezeraMovimientos + 1); i < rowTotalIntereses; i++) {
        worksheet[`F${i}`]['classContainer'] = 'border-r-4';
    }
    // TODO CABECERA MOVIMIENTOS DER
    for (const column of ['G', 'H', 'I', 'J', 'K']) {
        worksheet[`${column}${rowCabezeraMovimientos}`]['classContainer'] = 'bg-green-300';
        worksheet[`${column}${rowCabezeraMovimientos}`]['classText'] = 'text-black';
    }
    // TODO FOOTER IZQ
    worksheet[`E${rowTotalIntereses}`]['classText'] = 'font-bold';
    worksheet[`A${rowValInteresesRealesPagadosDer}`]['classText'] = 'font-bold';
    worksheet[`B${rowValInteresesRealesPagadosDer}`]['classText'] = 'font-bold';
    worksheet[`C${rowValInteresesRealesPagadosDer}`]['classText'] = 'font-bold';
    worksheet[`E${rowValInteresesRealesPagadosDer}`]['classText'] = 'font-bold';
    worksheet[`A${rowValDiferenciaCobrar}`]['classText'] = 'font-bold';
    worksheet[`A${rowValTasaRealPagada}`]['classText'] = 'font-bold';
    worksheet[`E${rowValTasaRealPagada}`]['classText'] = 'font-bold';
    worksheet[`E${rowValTasaNoRealizada}`]['classText'] = 'font-bold';
    worksheet[`E${rowValDiferenciaIntereses}`]['classText'] = 'font-bold';
    // TODO FOOTER DER
    worksheet[`J${rowTotalIntereses}`]['classText'] = 'font-bold';
    worksheet[`H${rowValInteresesRealesPagadosDer}`]['classText'] = 'font-bold';
    worksheet[`J${rowValInteresesRealesPagadosDer}`]['classText'] = 'font-bold';
    // TODO CELDAS INPUT
    // DIA, INGRESOS, PAGOS
    worksheet['B3']['isInput'] = true;
    worksheet[`B${rowValInteresesRealesPagadosIzq}`]['isInput'] = true;
    worksheet[`C${rowValInteresesRealesPagadosIzq}`]['isInput'] = true;
    worksheet[`E${rowValInteresesRealesPagadosIzq}`]['isInput'] = true;
    // TODO ID INPUTS
    worksheet['B3']['idInput'] = 'tasaEA';
    worksheet[`B${rowValInteresesRealesPagadosIzq}`]['idInput'] = 'rendimientosPeriodoIzq';
    worksheet[`C${rowValInteresesRealesPagadosIzq}`]['idInput'] = 'gananciaInteresesAdicionales';
    worksheet[`E${rowValInteresesRealesPagadosIzq}`]['idInput'] = 'rendimientosPeriodoPagadosMesSiguiente';
    return worksheet;
}

/**
 Aplica estilos personalizados a un objeto de datos en formato Excel.
 @param {Object} allData - Objeto de datos en formato Excel a los que se les aplicará los estilos personalizados.
 @param {Object} allData.worksheet - Objeto de datos en formato Excel que contiene los datos de las celdas.
 @param {number} allData.rowCabezeraMovimientos - Número de la fila de la cabecera de movimientos.
 @param {number} allData.rowTotalIntereses - Número de la fila del total de intereses.
 @param {number} allData.rowValInteresesRealesPagadosDer - Número de la fila del valor de intereses reales pagados de la columna derecha.
 @param {number} allData.rowValInteresesRealesPagadosIzq - Número de la fila del valor de intereses reales pagados de la columna izquierda.
 @param {number} allData.rowValDiferenciaCobrar - Número de la fila del valor de la diferencia a cobrar.
 @param {number} allData.rowValTasaRealPagada - Número de la fila del valor de la tasa real pagada.
 @param {number} allData.rowValTasaNoRealizada - Número de la fila del valor de la tasa no realizada.
 @param {number} allData.rowValDiferenciaIntereses - Número de la fila del valor de la diferencia de intereses.
 @returns {worksheet} - Hoja de cálculo con los estilos aplicados.
 */
function applyStyleExcel(allData) {
    const {
        worksheet,
        rowCabezeraMovimientos,
        rowTotalIntereses,
        rowValInteresesRealesPagadosDer,
        rowValInteresesRealesPagadosIzq,
        rowValDiferenciaCobrar,
        rowValTasaRealPagada,
        rowValTasaNoRealizada,
        rowValDiferenciaIntereses,
    } = allData;
    // TODO HELPERS
    let customBorder;
    const generateBorder = (width, color) => {
        const borderObject = {};
        for (const side of ['left', 'right', 'bottom', 'top']) {
            borderObject[side] = {
                style: width,
                color: {
                    rgb: color
                }
            };
        }
        return borderObject;
    };
    // TODO CABECERA IZQ
    worksheet['A1']['s'] = {
        font: {
            bold: true
        },
        border: generateBorder('medium', '000000')
    };
    worksheet['A2']['s'] = {
        font: {
            bold: true
        },
        fill: {
            fgColor: {
                rgb: "00CCFF"
            }
        },
        border: generateBorder('medium', '000000')
    };
    worksheet['A3']['s'] = {
        border: generateBorder('thin', '000000')
    };
    worksheet['A4']['s'] = {
        border: generateBorder('thin', '000000')
    };
    worksheet['B1']['s'] = {
        font: {
            bold: true
        },
        border: generateBorder('medium', '000000')
    };
    worksheet['B2']['s'] = {
        font: {
            bold: true
        },
        fill: {
            fgColor: {
                rgb: "00CCFF"
            }
        },
        border: generateBorder('medium', '000000'),
        alignment: {
            horizontal: 'center'
        }
    };
    customBorder = generateBorder('thin', '000000');
    customBorder['right'] = {
        style: 'medium',
        color: {
            rgb: '000000'
        }
    }
    worksheet['B3']['s'] = {
        font: {
            bold: true,
            color: {
                rgb: 'FF0000'
            },
            sz: '12'
        },
        fill: {
            fgColor: {
                rgb: "FFC000"
            }
        },
        border: customBorder,
        alignment: {
            horizontal: 'right'
        }
    };
    worksheet['B4']['s'] = {
        border: customBorder,
        alignment: {
            horizontal: 'right'
        }
    };
    worksheet['C2']['s'] = {
        font: {
            bold: true
        },
        fill: {
            fgColor: {
                rgb: "00CCFF"
            }
        },
        border: generateBorder('medium', '000000'),
        alignment: {
            horizontal: 'center'
        }
    };
    worksheet['D2']['s'] = {
        font: {
            bold: true,
            color: {
                rgb: 'FFFF00'
            },
            sz: '16'
        },
        fill: {
            fgColor: {
                rgb: "92D050"
            }
        },
        alignment: {
            horizontal: 'right'
        },
        border: generateBorder('medium', '000000'),
    };
    worksheet['E2']['s'] = {
        font: {
            bold: true
        },
        border: generateBorder('medium', '000000')
    };
    // CABECERA DER
    worksheet['G1']['s'] = {
        font: {
            bold: true
        },
        border: generateBorder('medium', '000000')
    };
    worksheet['G2']['s'] = {
        font: {
            bold: true
        },
        fill: {
            fgColor: {
                rgb: "00CCFF"
            }
        },
        border: generateBorder('medium', '000000')
    };
    customBorder = generateBorder('thin', '000000');
    customBorder['left'] = {
        style: 'medium',
        color: {
            rgb: '000000'
        }
    };
    worksheet['G3']['s'] = {
        border: customBorder
    };
    worksheet['G4']['s'] = {
        border: customBorder
    };
    worksheet['H1']['s'] = {
        font: {
            bold: true
        },
        border: generateBorder('medium', '000000')
    };
    worksheet['H2']['s'] = {
        font: {
            bold: true
        },
        fill: {
            fgColor: {
                rgb: "00CCFF"
            }
        },
        border: generateBorder('medium', '000000'),
        alignment: {
            horizontal: 'center'
        }
    };
    customBorder = generateBorder('thin', '000000');
    customBorder['right'] = {
        style: 'medium',
        color: {
            rgb: '000000'
        }
    }
    worksheet['H3']['s'] = {
        font: {
            bold: true,
            color: {
                rgb: 'FF0000'
            },
            sz: '12'
        },
        fill: {
            fgColor: {
                rgb: "00B050"
            }
        },
        border: customBorder,
        alignment: {
            horizontal: 'right'
        }
    };
    worksheet['H4']['s'] = {
        border: customBorder,
        alignment: {
            horizontal: 'right'
        }
    };
    worksheet['I2']['s'] = {
        font: {
            bold: true
        },
        fill: {
            fgColor: {
                rgb: "00CCFF"
            }
        },
        border: generateBorder('medium', '000000'),
        alignment: {
            horizontal: 'center'
        }
    };
    worksheet['J2']['s'] = {
        font: {
            bold: true,
            color: {
                rgb: 'FFFF00'
            },
            sz: '16'
        },
        fill: {
            fgColor: {
                rgb: "92D050"
            }
        },
        alignment: {
            horizontal: 'right'
        },
        border: generateBorder('medium', '000000'),
    };
    worksheet['K2']['s'] = {
        font: {
            bold: true
        },
        border: generateBorder('medium', '000000')
    };
    // TODO CABECERA MOVIMIENTOS IZQ
    for (const column of ['A', 'B', 'C', 'D', 'E', 'F']) {
        worksheet[`${column}${rowCabezeraMovimientos}`]['s'] = {
            font: {
                color: {
                    rgb: 'FFFFFF'
                }
            },
            fill: {
                bgColor: {
                    color: '000000'
                }
            }
        };
    }
    // TODO CABECERA MOVIMIENTOS DER
    for (const column of ['G', 'H', 'I', 'J', 'K']) {
        worksheet[`${column}${rowCabezeraMovimientos}`]['s'] = {
            font: {
                color: {
                    rgb: 'FFFFFF'
                }
            },
            fill: {
                bgColor: {
                    color: '000000'
                }
            }
        };
    }
    // TODO MOVIMIENTOS IZQ
    for (let i = (rowCabezeraMovimientos + 1); i < rowTotalIntereses; i++) {
        for (const column of ['A', 'B', 'C', 'D', 'E', 'F']) {
            if (column !== 'F')
                worksheet[`${column}${i}`]['s'] = {
                    border: generateBorder('thin', '000000'),
                    alignment: {
                        horizontal: 'right'
                    }
                };
            else {
                customBorder = generateBorder('thin', '000000');
                customBorder['right'] = {
                    style: 'medium',
                    color: {
                        rgb: '000000'
                    }
                };
                worksheet[`${column}${i}`]['s'] = {
                    border: customBorder,
                    alignment: {
                        horizontal: 'right'
                    }
                };
            }
        }
    }
    // TODO MOVIMIENTOS DER
    for (let i = (rowCabezeraMovimientos + 1); i < rowTotalIntereses; i++) {
        for (const column of ['G', 'H', 'I', 'J', 'K']) {
            if (column !== 'G')
                worksheet[`${column}${i}`]['s'] = {
                    border: generateBorder('thin', '000000'),
                    alignment: {
                        horizontal: 'right'
                    }
                };
            else {
                customBorder = generateBorder('thin', '000000');
                customBorder['left'] = {
                    style: 'medium',
                    color: {
                        rgb: '000000'
                    }
                };
                worksheet[`${column}${i}`]['s'] = {
                    border: customBorder,
                    alignment: {
                        horizontal: 'right'
                    }
                };
            }
        }
    }
    // TODO FOOTER IZQ
    customBorder = generateBorder('thin', '000000');
    customBorder['right'] = {
        style: 'medium',
        color: {
            rgb: '000000'
        }
    };
    customBorder['bottom'] = {
        style: 'medium',
        color: {
            rgb: '000000'
        }
    };
    worksheet[`E${rowTotalIntereses}`]['s'] = {
        font: {
            bold: true
        },
        alignment: {
            wrapText: true
        },
        border: customBorder
    };
    worksheet[`F${rowTotalIntereses}`]['s'] = {
        font: {
            bold: true
        },
        alignment: {
            horizontal: 'right'
        },
        fill: {
            fgColor: {
                rgb: 'FFC000'
            }
        },
        border: customBorder
    };
    customBorder = generateBorder('medium', '000000');
    customBorder['right'] = {
        style: 'thin',
        color: {
            rgb: '000000'
        }
    };
    worksheet[`A${rowValInteresesRealesPagadosDer}`]['s'] = {
        font: {
            bold: true
        },
        border: customBorder,
        alignment: {
            wrapText: true,
            vertical: 'center',
            horizontal: 'center'
        }
    };
    customBorder = generateBorder('thin', '000000');
    customBorder['top'] = {
        style: 'medium',
        color: {
            rgb: '000000'
        }
    }
    worksheet[`B${rowValInteresesRealesPagadosDer}`]['s'] = {
        font: {
            sz: '7'
        },
        border: customBorder,
        alignment: {
            wrapText: true,
            vertical: 'center',
            horizontal: 'center'
        }
    };
    customBorder = generateBorder('thin', '000000');
    customBorder['top'] = {
        style: 'medium',
        color: {
            rgb: '000000'
        }
    };
    worksheet[`C${rowValInteresesRealesPagadosDer}`]['s'] = {
        font: {
            sz: '7'
        },
        border: customBorder,
        alignment: {
            wrapText: true,
            vertical: 'center',
            horizontal: 'center'
        }
    };
    customBorder = generateBorder('thin', '000000');
    customBorder['top'] = {
        style: 'medium',
        color: {
            rgb: '000000'
        }
    };
    worksheet[`D${rowValInteresesRealesPagadosDer}`]['s'] = {
        font: {
            bold: true
        },
        border: customBorder,
    };
    customBorder = generateBorder('medium', '000000');
    for (const side of ['left', 'bottom']) {
        customBorder[side] = {
            style: 'thin',
            color: {
                rgb: '000000'
            }
        };
    }
    worksheet[`E${rowValInteresesRealesPagadosDer}`]['s'] = {
        font: {
            sz: '7'
        },
        border: customBorder,
        alignment: {
            wrapText: true,
            vertical: 'center',
            horizontal: 'center'
        }
    };
    customBorder = generateBorder('medium', '000000');
    for (const side of ['top', 'right']) {
        customBorder[side] = {
            style: 'thin',
            color: {
                rgb: '000000'
            }
        };
    }
    worksheet[`B${rowValInteresesRealesPagadosIzq}`]['s'] = {
        font: {
            bold: true,
            sz: '14'
        },
        fill: {
            fgColor: {
                rgb: "FFC000"
            }
        },
        alignment: {
            horizontal: 'center'
        },
        border: customBorder,
    };
    customBorder = generateBorder('thin', '000000');
    customBorder['bottom'] = {
        style: 'medium',
        color: {
            rgb: '000000'
        }
    };
    worksheet[`C${rowValInteresesRealesPagadosIzq}`]['s'] = {
        alignment: {
            horizontal: 'center'
        },
        border: customBorder,
    };
    customBorder = generateBorder('thin', '000000');
    customBorder['bottom'] = {
        style: 'medium',
        color: {
            rgb: '000000'
        }
    };
    worksheet[`D${rowValInteresesRealesPagadosIzq}`]['s'] = {
        alignment: {
            horizontal: 'center'
        },
        border: customBorder,
    };
    customBorder = generateBorder('thin', '000000');
    customBorder['bottom'] = {
        style: 'medium',
        color: {
            rgb: '000000'
        }
    };
    worksheet[`E${rowValInteresesRealesPagadosIzq}`]['s'] = {
        alignment: {
            horizontal: 'center'
        },
        border: customBorder,
    };
    customBorder = generateBorder('medium', '000000');
    customBorder['left'] = {
        style: 'thin',
        color: {
            rgb: '000000'
        }
    };
    worksheet[`F${rowValInteresesRealesPagadosIzq}`]['s'] = {
        alignment: {
            horizontal: 'right'
        },
        font: {
            bold: true
        },
        border: customBorder,
    };
    customBorder = generateBorder('medium', '000000');
    customBorder['right'] = {
        style: 'thin',
        color: {
            rgb: '000000'
        }
    };
    worksheet[`A${rowValDiferenciaCobrar}`]['s'] = {
        font: {
            bold: true
        },
        border: customBorder,
        alignment: {
            wrapText: true
        }
    };
    for (const column of ['B', 'C', 'D', 'E']) {
        worksheet[`${column}${rowValDiferenciaCobrar}`]['s'] = {
            border: {
                bottom: {
                    style: 'medium',
                    color: {
                        rgb: '000000'
                    }
                }
            },
        };
    }
    customBorder = generateBorder('medium', '000000');
    customBorder['left'] = {
        style: 'thin',
        color: {
            rgb: '000000'
        }
    };
    worksheet[`F${rowValDiferenciaCobrar}`]['s'] = {
        font: {
            bold: true
        },
        border: customBorder,
        alignment: {
            horizontal: 'right'
        },
    };
    worksheet[`A${rowValTasaRealPagada}`]['s'] = {
        font: {
            bold: true
        },
        alignment: {
            vertical: 'center',
            horizontal: 'center'
        },
        border: {
            bottom: {
                style: 'medium',
                color: {
                    rgb: '000000'
                }
            }
        },
    };
    worksheet[`B${rowValTasaRealPagada}`]['s'] = {
        font: {
            bold: true
        },
        border: {
            bottom: {
                style: 'medium',
                color: {
                    rgb: '000000'
                }
            },
            right: {
                style: 'medium',
                color: {
                    rgb: '000000'
                }
            }
        },
        alignment: {
            horizontal: 'right'
        },
    };
    for (const column of ['C', 'D']) {
        worksheet[`${column}${rowValTasaRealPagada}`]['s'] = {
            border: {
                bottom: {
                    style: 'medium',
                    color: {
                        rgb: '000000'
                    }
                }
            },
        };
    }
    worksheet[`E${rowValTasaRealPagada}`]['s'] = {
        font: {
            bold: true
        }
    };
    worksheet[`F${rowValTasaRealPagada}`]['s'] = {
        font: {
            bold: true
        },
        border: {
            right: {
                style: 'medium',
                color: {
                    rgb: '000000'
                }
            }
        },
        alignment: {
            horizontal: 'right'
        },
    };
    worksheet[`E${rowValTasaNoRealizada}`]['s'] = {
        fill: {
            fgColor: {
                rgb: 'FF0000'
            }
        },
        font: {
            bold: true
        },
        border: {
            left: {
                style: 'medium',
                color: {
                    rgb: '000000'
                }
            },
            bottom: {
                style: 'medium',
                color: {
                    rgb: '000000'
                }
            }
        },
    };
    worksheet[`F${rowValTasaNoRealizada}`]['s'] = {
        font: {
            bold: true
        },
        fill: {
            fgColor: {
                rgb: 'FF0000'
            }
        },
        border: {
            right: {
                style: 'medium',
                color: {
                    rgb: '000000'
                }
            },
            bottom: {
                style: 'medium',
                color: {
                    rgb: '000000'
                }
            }
        },
        alignment: {
            horizontal: 'right'
        },
    };
    customBorder = generateBorder('medium', '000000');
    delete customBorder['right'];
    worksheet[`E${rowValDiferenciaIntereses}`]['s'] = {
        font: {
            bold: true
        },
        alignment: {
            wrapText: true
        },
        border: customBorder,
    };
    customBorder = generateBorder('medium', '000000');
    delete customBorder['left'];
    worksheet[`F${rowValDiferenciaIntereses}`]['s'] = {
        font: {
            bold: true
        },
        border: customBorder,
        alignment: {
            horizontal: 'right'
        },
    };
    // TODO FOOTER DER
    customBorder = generateBorder('thin', '000000');
    customBorder['left'] = {
        style: 'medium',
        color: {
            rgb: '000000'
        }
    };
    customBorder['bottom'] = {
        style: 'medium',
        color: {
            rgb: '000000'
        }
    };
    for (const column of ['G', 'H', 'I']) {
        worksheet[`${column}${rowTotalIntereses}`]['s'] = {
            border: {
                'bottom': {
                    style: 'medium',
                    color: {
                        rgb: '000000'
                    }
                }
            }
        };
    }
    worksheet[`J${rowTotalIntereses}`]['s'] = {
        font: {
            bold: true
        },
        alignment: {
            wrapText: true
        },
        border: customBorder
    };
    customBorder = generateBorder('thin', '000000');
    for (const side of ['bottom', 'right']) {
        customBorder[side] = {
            style: 'medium',
            color: {
                rgb: '000000'
            }
        }
    }
    worksheet[`K${rowTotalIntereses}`]['s'] = {
        font: {
            bold: true,
            color: {
                rgb: 'FF0000'
            },
            sz: '12'
        },
        fill: {
            fgColor: {
                rgb: '00B050'
            }
        },
        alignment: {
            horizontal: 'right'
        },
        border: customBorder
    };
    customBorder = generateBorder('medium', '000000');
    worksheet[`H${rowValInteresesRealesPagadosDer}`]['s'] = {
        border: customBorder,
        alignment: {
            wrapText: true,
            vertical: 'center',
            horizontal: 'center'
        },
        font: {
            sz: '7'
        }
    };
    worksheet[`I${rowValInteresesRealesPagadosDer}`]['s'] = {
        border: customBorder,
        font: {
            bold: true
        },
        alignment: {
            horizontal: 'right'
        },
    };
    customBorder = generateBorder('medium', '000000');
    delete customBorder['right'];
    worksheet[`J${rowValInteresesRealesPagadosDer}`]['s'] = {
        border: customBorder,
        font: {
            bold: true
        }
    };
    customBorder = generateBorder('medium', '000000');
    delete customBorder['left'];
    worksheet[`K${rowValInteresesRealesPagadosDer}`]['s'] = {
        border: customBorder,
        font: {
            color: {
                rgb: 'FF0000'
            },
            bold: true
        },
        fill: {
            fgColor: {
                rgb: '66FF33'
            }
        },
        alignment: {
            horizontal: 'right'
        },
    };
    return worksheet;
}
